import * as React from "react";
import { alpha, styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Routes, Route } from "react-router-dom";
import AppTheme from "../../src/shared-theme/AppTheme";
import { xThemeComponents } from "./theme/customizations";
import { useNavigate } from "react-router-dom";
import SideMenu from "./components/SideMenu";
import MainGrid from "./components/MainGrid";
import FreightForm from "./components/Freight/FreightEntryForm";
import FreightList from "./components/Freight/FreightList";
import FreightFilter from "./components/Freight/FreightFilter";
import IssueForm from "./components/Issue/IssueForm";
import IssueList from "./components/Issue/IssueList";
import AddNewUser from "./components/User/AddNewUser";
import UserList from "./components/User/UserList";
import AddBranch from "./components/Branch/AddNewBranch";
import BranchList from "./components/Branch/BranchList";
import VehicleFrom from "./components/Vehicle/VehicleFrom";
import VehicleList from "./components/Vehicle/VehicleList";
// Expense
import ExpenseForm from "./components/Expense/ExpenseForm";
import ExpenseList from "./components/Expense/ExpenseList";
import DocumentAlert from "./components/Vehicle/DocumentAlert";
import AddParty from "./components/Party/AddParty";
import PartyList from "./components/Party/PartyList";
import FreightInvoice from "./components/Freight/FreightInvoice";
import TodayHistoryTable from "./components/History/TodayHistory";
import PastHistoryTable from "./components/History/PastHistory";
import Pendingforbill from "./components/Reports/Pendingforbill";
import Pendingforpayment from "./components/Reports/Pendingforpayment";
import Allfreight from "./components/Reports/Allfreight";
import Partyfreight from "./components/Reports/Partyfreight";
import Vehiclefreight from "./components/Reports/Vehiclefreight";
import Completefreight from "./components/Reports/Completefreight";
import Branchfreight from "./components/Reports/Branchfreight";
import PaymentPanding from "./components/Reports/PaymentPanding";
import PartyBill from "./components/Reports/PartyBill";
import BranchBill from "./components/Reports/Branchbill";
import PaymentList from "./components/Reports/PaymentList";
import PartyPayment from "./components/Reports/PartyPayment";
import BranchPayment from "./components/Reports/BranchPayment";
import BillCreationForm from "./components/Bill/BillCreation";
import BillList from "./components/Bill/BillList";
import FreightPaymentTable from "./components/Freight/FreightPayment";
import PaymentTable from "./components/Payment";
import Allbills from "./components/Reports/Allbills";
import Billpendingfreight from "./components/Reports/Billpendingfreight";
import Completebill from "./components/Reports/Completebill";
import BranchBills from "./components/Reports/Branchbill";
import PartyRestore from "./components/Restore/party";
import BranchRestore from "./components/Restore/branch";
import FreightRestore from "./components/Restore/freight";
import StaffRestore from "./components/Restore/staff";
import FreightPaymentReport from "./components/Reports/FreightPaymentReport";
import BillRestore from "./components/Restore/BillRestore";
import NotRecFreight from "./components/Freight/NotRecFreight";
import ExpenseReport from "./components/Reports/ExpenseReport";
import OurCustomerForm from "./components/LandingData/OurCustomerForm";
import CustomerList from "./components/LandingData/CustomerList";
import TestimonialForm from "./components/LandingData/TestimonialForm";
import TestimonialList from "./components/LandingData/TestimonialList";
import EnquiryDashboard from "./components/Enquiry Details/EnquiryDashboard";
import ConsigneeList from "./components/Conignee/ConsigneeList";
import ConsigneeForm from "./components/Conignee/ConsigneeForm";
import { SidebarProvider } from "./components/SidebarContext";
import AppNavbar, { DesktopTopBar } from "./components/AppNavbar";
import { ExpiringLRDialog } from "./components/Freight/ExpiringLRDialog";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProtectedRoute from "../components/ProtectedRoute";
import AccessDenied from "./components/AccessDenied";
import UserAccessManagement from "./components/User/UserAccessManagement";
import BrokerForm from "./components/Broker/BrokerForm";
import BrokerList from "./components/Broker/BrokerList";
import CustomBillCreation from "./components/Bill/CustomBillCreation";
import CustomBillList from "./components/Bill/CustomBillList";
import BlankLRList from "./components/Freight/BlankLRList";

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  height: "100vh",
  overflow: "hidden",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "280px",
    background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 100%)`,
    pointerEvents: "none",
    zIndex: 0,
  },
}));

const RouteContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  padding: 0,
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  minHeight: 0,
  animation: "fadeInUp 0.4s ease-out",
  "@keyframes fadeInUp": {
    from: {
      opacity: 0,
      transform: "translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

// Wrapper for OTHER pages that DO need padding (freight, bills, forms, etc.)
const PaddedRoute = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

export default function Dashboard(props) {
  const [freightData, setFreightData] = React.useState([]);
  const [selectedEntry, setSelectedEntry] = React.useState(null);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const navigate = useNavigate();

  // When edit icon is clicked
  const handleEdit = (entry, index) => {
    setSelectedEntry({ ...entry, index });
    setIsEditMode(true);
  };

  // When form is submitted
  const handleSave = (formData) => {
    if (isEditMode && formData.index !== undefined) {
      const updatedData = [...freightData];
      const { index, ...cleanedData } = formData;
      updatedData[index] = { ...cleanedData, index };
      setFreightData(updatedData);
      setIsEditMode(false);
      setSelectedEntry(null);
    } else {
      setFreightData([...freightData, formData]);
    }
    navigate("/freight-list");
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <SidebarProvider>
        <Box
          className="dashboard-shell"
          sx={{
            display: "flex",
            bgcolor: "background.default",
            minHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <SideMenu />
          <AppNavbar />

          <MainContent sx={{ mt: { xs: 8, md: 0 } }}>
            <DesktopTopBar />

            <RouteContainer>
              <ToastContainer
                position="top-right"
                autoClose={3000}
                style={{ zIndex: 99999, top: "70px" }}
              />
              <Routes>
                <Route path="/" element={<MainGrid />} />

                {/* Access Denied — always accessible (no guard) */}
                <Route
                  path="/access-denied"
                  element={
                    <PaddedRoute>
                      <AccessDenied />
                    </PaddedRoute>
                  }
                />

                {/* All other pages get padded wrapper + ProtectedRoute guard */}
                <Route
                  path="/freight-form"
                  element={
                    <ProtectedRoute pageKey="freight-form">
                      <PaddedRoute>
                        <FreightForm
                          initialData={selectedEntry}
                          isEdit={isEditMode}
                          onSave={handleSave}
                        />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/freight-form/edit/:id"
                  element={
                    <ProtectedRoute pageKey="freight-form">
                      <PaddedRoute>
                        <FreightForm />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/freight-list"
                  element={
                    <ProtectedRoute pageKey="freight-list">
                      <PaddedRoute>
                        <FreightList data={freightData} onEdit={handleEdit} />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/not-received-freight"
                  element={
                    <PaddedRoute>
                      <NotRecFreight />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/issue-form"
                  element={
                    <PaddedRoute>
                      <IssueForm />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/issue-list"
                  element={
                    <ProtectedRoute pageKey="issue-list">
                      <PaddedRoute>
                        <IssueList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/addnewstaff"
                  element={
                    <ProtectedRoute pageKey="stafflist">
                      <PaddedRoute>
                        <AddNewUser />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/addbranch"
                  element={
                    <PaddedRoute>
                      <AddBranch />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/branchlist"
                  element={
                    <PaddedRoute>
                      <BranchList />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/freightinvoice"
                  element={
                    <ProtectedRoute pageKey="freightinvoice">
                      <PaddedRoute>
                        <FreightInvoice />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/todayhistory"
                  element={
                    <PaddedRoute>
                      <TodayHistoryTable />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/pasthistory"
                  element={
                    <PaddedRoute>
                      <PastHistoryTable />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/pendingforbill"
                  element={
                    <ProtectedRoute pageKey="pendingforbill">
                      <PaddedRoute>
                        <Pendingforbill />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Pendingforpayment"
                  element={
                    <PaddedRoute>
                      <Pendingforpayment />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/Allfreight"
                  element={
                    <ProtectedRoute pageKey="Allfreight">
                      <PaddedRoute>
                        <Allfreight />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/freightfilter"
                  element={
                    <ProtectedRoute pageKey="freightfilter">
                      <PaddedRoute>
                        <FreightFilter />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Partyfreight"
                  element={
                    <ProtectedRoute pageKey="Partyfreight">
                      <PaddedRoute>
                        <Partyfreight />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Vehiclefreight"
                  element={
                    <ProtectedRoute pageKey="Vehiclefreight">
                      <PaddedRoute>
                        <Vehiclefreight />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Completefreight"
                  element={
                    <ProtectedRoute pageKey="Completefreight">
                      <PaddedRoute>
                        <Completefreight />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Branchfreight"
                  element={
                    <PaddedRoute>
                      <Branchfreight />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/VehicleList"
                  element={
                    <ProtectedRoute pageKey="VehicleList">
                      <PaddedRoute>
                        <VehicleList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/VehicleFrom"
                  element={
                    <ProtectedRoute pageKey="VehicleFrom">
                      <PaddedRoute>
                        <VehicleFrom />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/VehicleFrom/:id"
                  element={
                    <ProtectedRoute pageKey="VehicleFrom">
                      <PaddedRoute>
                        <VehicleFrom />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/DocumentAlert"
                  element={
                    <ProtectedRoute pageKey="DocumentAlert">
                      <PaddedRoute>
                        <DocumentAlert />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />

                {/* Expenses & Maintenance */}
                <Route
                  path="/ExpensesForm"
                  element={
                    <ProtectedRoute pageKey="ExpensesForm">
                      <PaddedRoute>
                        <ExpenseForm />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ExpensesList"
                  element={
                    <ProtectedRoute pageKey="ExpensesList">
                      <PaddedRoute>
                        <ExpenseList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ExpensesForm/:id"
                  element={
                    <ProtectedRoute pageKey="ExpensesForm">
                      <PaddedRoute>
                        <ExpenseForm />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/expense-report"
                  element={
                    <ProtectedRoute pageKey="expense-report">
                      <PaddedRoute>
                        <ExpenseReport />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/billlist"
                  element={
                    <ProtectedRoute pageKey="bill-list">
                      <PaddedRoute>
                        <BillList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/paymentpanding"
                  element={
                    <PaddedRoute>
                      <PaymentPanding />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/partyBill"
                  element={
                    <ProtectedRoute pageKey="partyBill">
                      <PaddedRoute>
                        <PartyBill />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/branchbill"
                  element={
                    <PaddedRoute>
                      <BranchBill />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/paymentlist"
                  element={
                    <PaddedRoute>
                      <PaymentList />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/partyPayment"
                  element={
                    <PaddedRoute>
                      <PartyPayment />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/branchpayment"
                  element={
                    <PaddedRoute>
                      <BranchPayment />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/bill-creation"
                  element={
                    <ProtectedRoute pageKey="bill-creation">
                      <PaddedRoute>
                        <BillCreationForm />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bill-list"
                  element={
                    <ProtectedRoute pageKey="bill-list">
                      <PaddedRoute>
                        <BillList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/freightpayment"
                  element={
                    <ProtectedRoute pageKey="freightpayment">
                      <PaddedRoute>
                        <FreightPaymentTable />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment"
                  element={
                    <PaddedRoute>
                      <PaymentTable />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/Allbills"
                  element={
                    <ProtectedRoute pageKey="Allbills">
                      <PaddedRoute>
                        <Allbills />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Billpendingfreight"
                  element={
                    <ProtectedRoute pageKey="Billpendingfreight">
                      <PaddedRoute>
                        <Billpendingfreight />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Completebill"
                  element={
                    <ProtectedRoute pageKey="Completebill">
                      <PaddedRoute>
                        <Completebill />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/BranchBills"
                  element={
                    <PaddedRoute>
                      <BranchBills />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/freight/payment/report"
                  element={
                    <ProtectedRoute pageKey="freight/payment/report">
                      <PaddedRoute>
                        <FreightPaymentReport />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/restore/branch"
                  element={
                    <PaddedRoute>
                      <BranchRestore />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/restore/party"
                  element={
                    <PaddedRoute>
                      <PartyRestore />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/restore/staff"
                  element={
                    <PaddedRoute>
                      <StaffRestore />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/restore/freight"
                  element={
                    <PaddedRoute>
                      <FreightRestore />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/restore/bill"
                  element={
                    <PaddedRoute>
                      <BillRestore />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/customer-form"
                  element={
                    <ProtectedRoute pageKey="customer-form">
                      <PaddedRoute>
                        <OurCustomerForm />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customer-list"
                  element={
                    <ProtectedRoute pageKey="customer-list">
                      <PaddedRoute>
                        <CustomerList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/testimonial-form"
                  element={
                    <ProtectedRoute pageKey="testimonial-form">
                      <PaddedRoute>
                        <TestimonialForm />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/testimonial-list"
                  element={
                    <ProtectedRoute pageKey="testimonial-list">
                      <PaddedRoute>
                        <TestimonialList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/enquiry-details"
                  element={
                    <ProtectedRoute pageKey="enquiry-details">
                      <PaddedRoute>
                        <EnquiryDashboard />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partylist"
                  element={
                    <ProtectedRoute pageKey="partylist">
                      <PaddedRoute>
                        <PartyList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/addparty"
                  element={
                    <ProtectedRoute pageKey="partylist">
                      <PaddedRoute>
                        <AddParty />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/consignee-list"
                  element={
                    <ProtectedRoute pageKey="consignee-list">
                      <PaddedRoute>
                        <ConsigneeList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-consignee"
                  element={
                    <ProtectedRoute pageKey="consignee-list">
                      <PaddedRoute>
                        <ConsigneeForm />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stafflist"
                  element={
                    <ProtectedRoute pageKey="stafflist">
                      <PaddedRoute>
                        <UserList />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/expiring-lr"
                  element={
                    <PaddedRoute>
                      <ExpiringLRDialog />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/broker-form"
                  element={
                    <PaddedRoute>
                      <BrokerForm />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/broker-list"
                  element={
                    <PaddedRoute>
                      <BrokerList />
                    </PaddedRoute>
                  }
                />

                <Route
                  path="/custom-bill-creation"
                  element={
                    <PaddedRoute>
                      <CustomBillCreation />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/custom-bill-list"
                  element={
                    <PaddedRoute>
                      <CustomBillList />
                    </PaddedRoute>
                  }
                />
                <Route
                  path="/custom-bill/edit/:id"
                  element={
                    <PaddedRoute>
                      <CustomBillCreation />
                    </PaddedRoute>
                  }
                />

                <Route
                  path="/blank-lr-list"
                  element={
                    <PaddedRoute>
                      <BlankLRList/>
                    </PaddedRoute>
                  }
                />

                {/* Admin — User Access Management */}
                <Route
                  path="/user-access"
                  element={
                    <ProtectedRoute pageKey="user-access">
                      <PaddedRoute>
                        <UserAccessManagement />
                      </PaddedRoute>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </RouteContainer>
          </MainContent>
        </Box>
      </SidebarProvider>
    </AppTheme>
  );
}
